import React, { useMemo } from 'react';
import '../styles.css';
import { getMetadataBundleForStaticPage } from '../metadata/SEOmetadata.js';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  const pageMetadata = useMemo(() => getMetadataBundleForStaticPage('about'), []);

  return (
    <main>
      <Helmet>
        <title>About HermesReads | Library, Publishing Infrastructure, EPUB Conversion, and Metadata Work</title>
        <meta
          name="description"
          content="HermesReads is a digital library and publishing infrastructure project focused on public-domain books, revived editions, EPUB conversion, metadata enrichment, catalog cleanup, translation projects, and book-production workflows."
        />
        <meta
          name="keywords"
          content="HermesReads, digital library, publishing infrastructure, EPUB conversion, ebook conversion, metadata enrichment, book metadata services, ONIX preparation, book production, public domain books, revived editions, backlist recovery, catalog cleanup, translation projects, ebook publishing, print on demand preparation"
        />

        <meta property="og:title" content="About HermesReads | Library, Publishing Infrastructure, EPUB Conversion, and Metadata Work" />
        <meta
          property="og:description"
          content="HermesReads combines a public digital library with publishing infrastructure, EPUB conversion, metadata enrichment, catalog cleanup, translation projects, and book-production workflows."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.hermesreads.com/about" />
        <meta property="og:image" content="https://www.hermesreads.com/android-chrome-512x512.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About HermesReads | EPUB Conversion, Metadata, Publishing, and Digital Library" />
        <meta
          name="twitter:description"
          content="HermesReads is a digital library and publishing infrastructure project for public-domain books, EPUB conversion, metadata enrichment, catalog cleanup, and revived editions."
        />
        <meta name="twitter:image" content="https://www.hermesreads.com/android-chrome-512x512.png" />

        <link rel="canonical" href="https://www.hermesreads.com/about" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About HermesReads",
            "url": "https://www.hermesreads.com/about",
            "description": "HermesReads is a digital library and publishing infrastructure project focused on public-domain books, revived editions, EPUB conversion, metadata enrichment, catalog cleanup, translation projects, and book-production workflows.",
            "mainEntity": {
              "@type": "Organization",
              "name": "HermesReads",
              "url": "https://www.hermesreads.com/",
              "logo": "https://www.hermesreads.com/android-chrome-512x512.png",
              "description": "HermesReads combines a public digital library with EPUB conversion, metadata enrichment, ONIX preparation, publishing support, public-domain editions, and backlist recovery."
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "EPUB conversion, metadata enrichment, and publishing infrastructure services",
            "description": "HermesReads provides EPUB conversion, ebook formatting, OCR cleanup, metadata enrichment, ONIX preparation, distributor upload preparation, backlist recovery, catalog cleanup, and print-on-demand preparation.",
            "provider": {
              "@type": "Organization",
              "name": "HermesReads",
              "url": "https://www.hermesreads.com/"
            },
            "areaServed": "Worldwide",
            "serviceType": [
              "EPUB conversion",
              "ebook formatting",
              "OCR cleanup",
              "metadata enrichment",
              "ONIX metadata preparation",
              "backlist recovery",
              "catalog cleanup",
              "public domain publishing preparation",
              "print-on-demand preparation"
            ],
            "url": "https://www.hermesreads.com/services"
          })}
        </script>
      </Helmet>

      <section className="about-section">
        <div className="about-container">
          <h2>About Hermes Reads</h2>
          <p>
            Hermes Reads is a digital library and publishing infrastructure project focused on classic, neglected, and hard-to-find literature.
            We maintain a public reading platform while also developing professional workflows for book conversion, original translation,
            metadata enrichment, and edition preparation across digital and print.
          </p>

          <h3>Public Library and Editorial Work</h3>
          <p>
            On the public side, Hermes Reads offers a growing library of public-domain works, historical texts, and editorial projects
            across languages, genres, and periods. We focus on making literature available in clean, readable digital editions rather
            than leaving important works trapped in raw scans, inconsistent files, or poorly structured source material.
          </p>
          <p>
            Hermes Reads also produces original translations and newly prepared editions of works that are unavailable, out of print,
            or not meaningfully accessible to contemporary readers. This includes translation, text cleanup, structural repair,
            formatting, and editorial preparation for modern reading and distribution.
          </p>

          <h3>Publishing Conversion and Backlist Support</h3>
          <p>
            Alongside the public library, Hermes Reads functions as a practical publishing infrastructure for publishers, archives,
            and other partners who need books converted, repaired, enriched, or extended across formats. We help turn print books into
            EPUBs, prepare ebooks for print workflows, normalize legacy files, and reactivate backlist titles that exist in incomplete,
            inconsistent, or outdated forms.
          </p>
          <p>
            This work is supported by sophisticated internal pipelines developed specifically for large-scale book handling. These
            workflows allow us to move beyond one-off conversion and instead handle structure repair, metadata refinement, edition
            preparation, validation, and delivery in a systematic and repeatable way.
          </p>

          <h3>What We Work With</h3>
          <p>
            Our work covers the full chain from source text to distribution-ready output. Depending on the project, this includes:
          </p>
          <ul>
            <li>Print-to-EPUB conversion and EPUB-to-print preparation</li>
            <li>EPUB production, cleanup, validation, and standards compliance</li>
            <li>Metadata enrichment and normalization for discovery and distribution</li>
            <li>Edition structuring across digital and print formats</li>
            <li>Cover preparation for storefront, library, and app environments</li>
            <li>Backlist recovery, catalog cleanup, and format expansion</li>
          </ul>

          <h3>Metadata and Catalog Preparation</h3>
          <p>
            A major part of Hermes Reads is metadata work. We do not treat books as isolated files, but as catalog assets that need
            to function properly across systems. Our workflows are designed to improve discoverability, consistency, and professional
            usability through rich descriptive metadata, structured exports, and preparation for integration into retail, library,
            educational, and institutional environments.
          </p>

          <h3>Institutional and Publisher Collaboration</h3>
          <p>
            Hermes Reads works with publishers and other partners who need reliable, standards-aware content prepared for modern use.
            This may involve converting print-only titles into ebooks, bringing ebook-only titles into print-ready form, improving
            metadata quality across older catalogs, or preparing structured files for smoother circulation across current distribution channels.
          </p>
          <p>
            We are especially interested in helping publishers close format gaps, strengthen their metadata, and recover value from
            backlists that were never fully prepared for digital and print interoperability.
          </p>

          <h3>Enhanced Search and User Experience</h3>
          <p>
            Readers can explore books by title, author, keyword, genre, movement, and related categories, making it easier to move
            across periods, languages, and literary traditions.
          </p>
          <p>Our platform also offers:</p>
          <ul>
            <li>A clean, distraction-free reading interface</li>
            <li>Multilingual originals and carefully prepared translations</li>
            <li>Advanced categorization for faster discovery</li>
            <li>Regular additions across Nordic and European languages</li>
          </ul>

          <h3>Coming Soon: Hermes Reads App</h3>
          <p>
            We are developing a mobile app for iOS and Android with synced libraries, offline reading, and curated discovery,
            extending the Hermes Reads experience to phones and tablets.
          </p>

          <h3>Contributors and Community</h3>
          <p>
            Hermes Reads grows through collaboration with editors, translators, metadata specialists, archives, and academic partners
            working to preserve and prepare rare, neglected, and out-of-print literature for contemporary use.
          </p>
          <p>
            If you are interested in contributing to this work, please reach out through our contact page.
          </p>

          <h3>Current Focus</h3>
          <p>
            Hermes Reads is developing both as a public reading platform and as a production base for modern publishing work. Our
            current focus includes original translations, curated editions, large-scale digital text preparation, metadata-rich catalog
            development, and conversion support for publishers seeking stronger ebooks, cleaner records, and more usable print and digital files.
          </p>

          <h3>Ethical Commitment and Accessibility</h3>
          <p>
            We prioritize accessibility, sustainability, and user privacy. Hermes Reads is built to improve access to literature while
            producing digital texts and publishing assets that are structured, readable, and durable.
          </p>

          <h3>Editorial Neutrality</h3>
          <p>
            Our archive preserves historical texts as originally published, including works that may reflect outdated or controversial
            views. Inclusion does not imply endorsement; our aim is to preserve, prepare, and present the historical record in a form
            that can be read and used responsibly.
          </p>
        </div>
      </section>

      <section className="about-section">
        <div className="about-container">
          <h2>Terms of Use</h2>
          <p>
            <strong>1. Scope and Audience</strong>
          </p>
          <p>
            Hermes Reads is a curated digital library dedicated to preserving and sharing public domain literature. Our collection features texts freely accessible from various public repositories and digital archives, alongside carefully edited editions, original translations, and selected publications available through affiliate partnerships, including platforms such as Amazon.
          </p>
          <p>
            This platform is designed primarily for personal, educational, and non-commercial use. While the majority of materials available on Hermes Reads are in the public domain, they originate from diverse contributors and digitization partners, some of whom may retain certain rights or request attribution. To respect these varying terms, we kindly request users not to copy or redistribute content directly from Hermes Reads without explicit permission.
          </p>
          <p>
            Mass redistribution, commercial publication, or substantial modification of content from Hermes Reads is strictly prohibited without prior written consent.
          </p>
          <p>
            <strong>2. Public Domain Texts</strong>
          </p>
          <p>
            Hermes Reads provides access to numerous books believed to be in the public domain—meaning their copyrights have expired, been explicitly forfeited, or were never subject to protection. Generally, these texts can be freely used under U.S. law (Title 17, Chapter 1, Section 105) and relevant EU copyright directives, including Directive 2001/29/EC.
          </p>
          <p>
            However, Hermes Reads cannot definitively guarantee the legal status of every text across all jurisdictions. Certain materials may be subject to local regulations or specific terms set forth by original digitization partners or content providers.
          </p>
          <p>
            We therefore strongly encourage users to independently verify the copyright status and terms of use for any material they wish to reuse, adapt, or distribute—especially for commercial purposes or usage outside of the U.S. and EU. If you have any doubts regarding specific content, we recommend consulting the original source archive or reaching out to us for guidance.
          </p>
          <p><strong>3. Upcoming Editions and Special Projects</strong></p>
          <p>
            In addition to our free collection, Hermes Reads is working on a series of editorial projects and translated editions. These include:
          </p>
          <ul>
            <li>Original translations and annotated editions prepared by our editorial team.</li>
            <li>Special Hermes Reads editions that will be distributed through carefully selected platforms once finalized.</li>
          </ul>
          <p>
            While some of these may become available as purchasable editions in the future, they are currently under active development. We aim to make them accessible in ways that best support the sustainability of the project and the continued growth of the archive.
          </p>
          <p>
            <strong>4. Hermes Reads Branding</strong>
          </p>
          <p>
            The name Hermes Reads, our logo, and associated branding elements are protected under trademark and intellectual property laws, including Regulation (EU) 2017/1001 and U.S. Trademark Law (Title 15 §1125).
          </p>
          <p>
            These elements must not be copied, modified, reproduced, or distributed without explicit written authorization. Unauthorized use may result in legal consequences, including civil or criminal action.
          </p>
          <p>
            <strong>5. Institutional Access</strong>
          </p>
          <p>
            Educational institutions, researchers, libraries, and developers interested in structured or bulk access (such as large-scale downloads, metadata corpora, or API integrations) are encouraged to contact Hermes Reads directly to arrange specific agreements and clarify appropriate usage terms.
          </p>
          <p>
            <strong>6. Disclaimers</strong>
          </p>
          <p>
            Hermes Reads strives to provide accurate and comprehensive resources but cannot guarantee the accuracy, completeness, or legal validity of third-party metadata, translations, or the contents of external archives. All content provided by Hermes Reads is delivered "as-is" for informational, educational, and cultural purposes.
          </p>
          <p>
            Users should independently assess the suitability, accuracy, and legal compliance of any content accessed through our site.
          </p>
          <p>
            <strong>7. General Terms</strong>
          </p>
          <p>
            By accessing and using Hermes Reads, you acknowledge and agree to adhere to these Terms of Use. Users are solely responsible for ensuring their compliance with all applicable intellectual property and copyright laws relevant to their jurisdiction.
          </p>
          <p>
            Hermes Reads reserves the right to update, modify, or clarify these terms at any time. Any changes will become effective immediately upon their publication on this page. Users are advised to review these terms periodically for updates.
          </p>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;